/**
 * cvt-performance.ts - Módulo Canônico de Cinemática & Upgrades de Performance do CVT
 * Domínio: Manutenção Técnica e Otimização da Honda ADV 150 (Projeto K0W / Bloco KF43E)
 * Filosofia Fábio Akita: Boring Technology, Deep Modules, Zero Mocks e Fail-Visible.
 */

export interface PerformancePartItem {
  id: string;
  name: string;
  partNumberOrModel: string;
  category: "polia" | "rolete" | "correia" | "vela" | "guias" | "consumivel";
  brand: string;
  technicalSpec: string;
  oemReference?: string;
  expectedGain: string;
  estimatedPriceBrl: number;
  criticalGotcha: string;
}

export interface UpgradeStageConfig {
  stage: 1 | 2;
  title: string;
  summary: string;
  targetMaxSpeedGpsKmh: { min: number; max: number };
  targetMaxSpeedDashKmh: { min: number; max: number };
  stockComparisonPercentage: number; // ex: +10%
  cruisingRpmAt100Kmh: number;
  engineSafetyLevel: "100% Original e Seguro" | "Moderado (Giro Estendido)";
  reversibility: "Totalmente Plug-and-Play" | "Requer Regravação de ECU";
  estimatedTotalCostBrl: number;
  keyPartsIncluded: string[];
}

export interface CvtKinematicsResult {
  rpm: number;
  cvtRatio: number; // ex: 0.82 (stock overdrive) ou 0.74 (tuned overdrive)
  effectivePrimaryRadiusMm: number;
  effectiveSecondaryRadiusMm: number;
  realSpeedGpsKmh: number;
  indicatedDashSpeedKmh: number;
  speedDeltaKmHVsStock: number;
}

export interface RollerSimulationResult {
  rollerWeightGrams: number;
  launchResponse:
    | "pesada / lerda"
    | "equilibrada / original"
    | "ágil / esportiva"
    | "muito agressiva (berrando)";
  topSpeedPotential:
    | "reduzida (não abre)"
    | "máxima (+10% com polia)"
    | "padrão OEM (110 km/h)"
    | "comprometida por sobregiro";
  recommendedApplication: string;
  isRecommendedForTopSpeedGoal: boolean;
}

/**
 * Constantes Físicas e Cinemáticas da Honda ADV 150 (Roda 13 polegadas 130/70-13)
 */
export const ADV150_PHYSICAL_CONSTANTS = {
  // Pneu Traseiro 130/70-13: Raio = (13 * 25.4 / 2) + (130 * 0.70) = 165.1 + 91.0 = 256.1 mm
  rearWheelRadiusMm: 256.1,
  rearWheelCircumferenceMeters: (2 * Math.PI * 256.1) / 1000, // ~1.6091 metros por volta
  // Relação fixa do redutor traseiro por engrenagens da ADV 150 (redução secundária final)
  finalDriveGearboxRatio: 9.0,
  // Rotação máxima de potência original (13,2 cv)
  peakPowerRpm: 8500,
  // Corte de giro de fábrica da ECU Keihin original
  stockRevLimiterRpm: 9200,
  // Relação de transmissão do variador original OEM no batente máximo (overdrive)
  stockOverdriveRatio: 0.82,
  // Relação de transmissão do variador preparado com subida total da correia (Belt Up)
  tunedOverdriveRatio: 0.74,
  // Relação de saída curta original (arrancada)
  stockLaunchRatio: 2.6,
  // Relação de saída com roletes deslizantes TechPulley / rebaixo central
  tunedLaunchRatio: 2.7,
  // Fator de desvio regulamentar do velocímetro de painel (ECE R39 / CONTRAN ~ +5.8%)
  speedometerCalibrationFactor: 1.058,
} as const;

/**
 * Catálogo Canônico de Peças de Alta Performance para o Estágio 1 (+10% de Velocidade)
 */
export const ADV150_PERFORMANCE_PARTS: ReadonlyArray<PerformancePartItem> = [
  {
    id: "part-polias-cnc-preparadas",
    name: "Kit de Polias Preparadas em Torno CNC (Face Móvel + Face Fixa)",
    partNumberOrModel: "CNC-RACING-13.8DEG",
    category: "polia",
    brand: "Base OEM Honda Usinada (Tey Tey / WR Racing / Pitico)",
    technicalSpec:
      "Ângulo usinado de 13,8° a 14,0°, prolongamento das canaletas de roletes até a borda e alívio do batente da rampa.",
    oemReference: "22110-K0W-B00 / 22102-K97-T01",
    expectedGain:
      "Permite à correia subir +2,5 mm na polia dianteira (ganho de relação overdrive de 0,82 para 0,74).",
    estimatedPriceBrl: 320.0,
    criticalGotcha:
      "Exige torquímetro a 59 N.m na porca M14 e protocolo mandatório de Belt Sinking para não esmagar a correia.",
  },
  {
    id: "part-roletes-techpulley-18g",
    name: "Jogo de 6 Roletes Deslizantes TechPulley Flying Rollers II (20x15 mm - 18g)",
    partNumberOrModel: "FR2015-18G",
    category: "rolete",
    brand: "TechPulley (ou Dr. Pulley Sliding Rollers)",
    technicalSpec:
      "Geometria poligonal com abas anti-rotação em polímero autolubrificante de alto ponto de fusão. Peso 18,0 g cada.",
    oemReference: "22123-K0W-B00 (OEM 21,0 g)",
    expectedGain:
      "Elimina facetamento dos roletes, melhora a saída e abre a rampa 1,5 mm mais na alta.",
    estimatedPriceBrl: 190.0,
    criticalGotcha:
      "MONTAGEM 100% A SECO! Jamais passar graxa nos roletes. Respeitar o lado correto das abas durante a inserção.",
  },
  {
    id: "part-correia-vbelt-oem-nova",
    name: "Correia de Transmissão V-Belt Genuína Honda Bando (Nova 19,5 mm)",
    partNumberOrModel: "23100-K97-T01",
    category: "correia",
    brand: "Honda Dream / Bando Double Cogged",
    technicalSpec:
      "Correia dentada dupla com cordões internos de tração em Kevlar/aramida. Largura nominal perfeita de 19,5 mm.",
    oemReference: "23100-K97-T01",
    expectedGain:
      "Garante subida total na polia primária. Correia com desgaste de 1 mm rouba até 8 km/h de velocidade final.",
    estimatedPriceBrl: 160.0,
    criticalGotcha:
      "Descartar se largura for ≤ 18,5 mm. Verificar sentido de rotação da seta se houver inscrição direcional.",
  },
  {
    id: "part-slide-pieces-oem",
    name: "Jogo de Cursores Plásticos Guia da Placa de Rampa (3 unidades)",
    partNumberOrModel: "22011-K97-T00",
    category: "guias",
    brand: "Honda Dream OEM / FCC",
    technicalSpec:
      "Polímero de engenharia com tolerância de folga zero nas 3 torres da placa de rampa.",
    oemReference: "22011-K97-T00",
    expectedGain:
      "Previne oscilação angular da rampa em alta rotação, eliminando vibrações a 8.500 rpm.",
    estimatedPriceBrl: 45.0,
    criticalGotcha:
      "Substituir sempre que trocar os roletes para manter alinhamento milimétrico do variador.",
  },
  {
    id: "part-vela-ngk-iridium",
    name: "Vela de Ignição Especial NGK Laser Iridium / MotoDX",
    partNumberOrModel: "CPR8EAIX-9 / CPR8EDX-9S",
    category: "vela",
    brand: "NGK Spark Plugs Japan",
    technicalSpec:
      "Eletrodo central de irídio ultrafino de 0,6 mm com pastilha de platina no eletrodo de massa. Rosca M10 x 1,25.",
    oemReference: "31918-K97-T01 (NGK MR8K-9 original)",
    expectedGain:
      "Queima otimizada e faísca estável sem falhas sob carga máxima e rotação contínua de 8.500 rpm.",
    estimatedPriceBrl: 90.0,
    criticalGotcha:
      "Rosquear à mão por 4 voltas antes da ferramenta; aperto estrito a 16 N.m com chave de vela de 16 mm.",
  },
];

/**
 * Matriz Comparativa de Estágios de Preparação
 */
export const ADV150_UPGRADE_STAGES: ReadonlyArray<UpgradeStageConfig> = [
  {
    stage: 1,
    title: "Estágio 1: Otimização Mecânica do CVT V-Matic (Recomendado)",
    summary:
      "Substituição das polias originais por polias com grau usinado CNC (13,8°), roletes TechPulley 18g/19g, correia OEM nova 19,5 mm e vela Iridium. Mantém o motor original intacto e preserva 100% da confiabilidade.",
    targetMaxSpeedGpsKmh: { min: 120, max: 123 },
    targetMaxSpeedDashKmh: { min: 126, max: 130 },
    stockComparisonPercentage: 10.0,
    cruisingRpmAt100Kmh: 6950, // Cai ~650 rpm em relação ao original a 100 km/h
    engineSafetyLevel: "100% Original e Seguro",
    reversibility: "Totalmente Plug-and-Play",
    estimatedTotalCostBrl: 805.0,
    keyPartsIncluded: [
      "Kit Polias CNC 13,8°",
      "Roletes TechPulley 18g",
      "Correia OEM 23100-K97-T01",
      "Vela Iridium CPR8EAIX-9",
      "Cursores Guia 22011-K97-T00",
    ],
  },
  {
    stage: 2,
    title: "Estágio 2: Combo Performance (CVT Otimizado + Remap ECU Keihin)",
    summary:
      "Combinação do Estágio 1 com remapeamento eletrônico da ECU original Keihin. Eleva o corte de giro de 9.200 para 10.000 rpm, recalibra ponto de ignição e mapa AFR para aceleração e velocidade sustentada em descidas.",
    targetMaxSpeedGpsKmh: { min: 124, max: 127 },
    targetMaxSpeedDashKmh: { min: 132, max: 135 },
    stockComparisonPercentage: 13.5,
    cruisingRpmAt100Kmh: 6950,
    engineSafetyLevel: "Moderado (Giro Estendido)",
    reversibility: "Requer Regravação de ECU",
    estimatedTotalCostBrl: 1355.0, // R$ 805 peças + R$ 550 remap
    keyPartsIncluded: [
      "Peças completas do Estágio 1",
      "Serviço de Remap de Injeção/Ignição",
      "Ajuste de corte de giro para 10.000 rpm",
    ],
  },
];

/**
 * Calcula a cinemática exata de velocidade teórica para um determinado regime de rotação e relação de CVT
 */
export function calculateCvtSpeed(
  engineRpm: number,
  cvtRatio: number = ADV150_PHYSICAL_CONSTANTS.stockOverdriveRatio,
): CvtKinematicsResult {
  if (engineRpm <= 0) {
    throw new Error("RPM do motor deve ser positivo.");
  }
  if (cvtRatio <= 0) {
    throw new Error("Relação do CVT deve ser positiva.");
  }

  // Rotação do eixo da polia secundária (RPM motor / relação do CVT)
  const secondaryShaftRpm = engineRpm / cvtRatio;

  // Rotação da roda traseira através do redutor final
  const rearWheelRpm = secondaryShaftRpm / ADV150_PHYSICAL_CONSTANTS.finalDriveGearboxRatio;

  // Metros por minuto
  const metersPerMinute = rearWheelRpm * ADV150_PHYSICAL_CONSTANTS.rearWheelCircumferenceMeters;

  // km/h real (GPS)
  const realSpeedGpsKmh = Number(((metersPerMinute * 60) / 1000).toFixed(1));

  // km/h indicado no painel digital
  const indicatedDashSpeedKmh = Number(
    (realSpeedGpsKmh * ADV150_PHYSICAL_CONSTANTS.speedometerCalibrationFactor).toFixed(0),
  );

  // Comparação com a velocidade original no mesmo RPM com relação original 0,82
  const stockSecondaryRpm = engineRpm / ADV150_PHYSICAL_CONSTANTS.stockOverdriveRatio;
  const stockWheelRpm = stockSecondaryRpm / ADV150_PHYSICAL_CONSTANTS.finalDriveGearboxRatio;
  const stockGpsSpeed =
    (stockWheelRpm * ADV150_PHYSICAL_CONSTANTS.rearWheelCircumferenceMeters * 60) / 1000;
  const speedDeltaKmHVsStock = Number((realSpeedGpsKmh - stockGpsSpeed).toFixed(1));

  // Raio efetivo primário estimado (diâmetro máximo útil 115 mm)
  // cvtRatio = R2 / R1. Se R2 ~ 85 mm (afundado na secundária):
  const effectiveSecondaryRadiusMm = 42.5;
  const effectivePrimaryRadiusMm = Number((effectiveSecondaryRadiusMm / cvtRatio).toFixed(1));

  return {
    rpm: engineRpm,
    cvtRatio: Number(cvtRatio.toFixed(3)),
    effectivePrimaryRadiusMm,
    effectiveSecondaryRadiusMm,
    realSpeedGpsKmh,
    indicatedDashSpeedKmh,
    speedDeltaKmHVsStock,
  };
}

/**
 * Calcula a rotação necessária do motor para manter 100 km/h de cruzeiro real
 */
export function calculateCruisingRpmAtSpeed(
  targetSpeedKmh = 100,
  isTunedCvt = false,
): { cruisingRpm: number; cvtRatio: number; rpmDifferenceVsStock: number } {
  const cvtRatio = isTunedCvt
    ? ADV150_PHYSICAL_CONSTANTS.tunedOverdriveRatio
    : ADV150_PHYSICAL_CONSTANTS.stockOverdriveRatio;

  // Inversão da fórmula de velocidade:
  // SpeedKmh = (RPM / cvtRatio / finalDrive * circumference * 60) / 1000
  // RPM = (SpeedKmh * 1000 / 60) * cvtRatio * finalDrive / circumference
  const metersPerMinute = (targetSpeedKmh * 1000) / 60;
  const rearWheelRpm = metersPerMinute / ADV150_PHYSICAL_CONSTANTS.rearWheelCircumferenceMeters;
  const secondaryShaftRpm = rearWheelRpm * ADV150_PHYSICAL_CONSTANTS.finalDriveGearboxRatio;
  const cruisingRpm = Math.round(secondaryShaftRpm * cvtRatio);

  const stockMetersPerMin = (targetSpeedKmh * 1000) / 60;
  const stockWheelRpm = stockMetersPerMin / ADV150_PHYSICAL_CONSTANTS.rearWheelCircumferenceMeters;
  const stockRpm = Math.round(
    stockWheelRpm *
      ADV150_PHYSICAL_CONSTANTS.finalDriveGearboxRatio *
      ADV150_PHYSICAL_CONSTANTS.stockOverdriveRatio,
  );

  return {
    cruisingRpm,
    cvtRatio,
    rpmDifferenceVsStock: cruisingRpm - stockRpm,
  };
}

/**
 * Simulador de resposta dinâmica e adequação da gramatura de roletes para o objetivo de velocidade final
 */
export function simulateRollerResponse(rollerWeightGrams: number): RollerSimulationResult {
  if (rollerWeightGrams < 10 || rollerWeightGrams > 25) {
    throw new Error("Peso do rolete fora da faixa física admissível (10g a 25g).");
  }

  if (rollerWeightGrams < 15) {
    return {
      rollerWeightGrams,
      launchResponse: "muito agressiva (berrando)",
      topSpeedPotential: "reduzida (não abre)",
      recommendedApplication:
        "Arrancadas curtas de semáforo urbano. Motor gira acima de 9.000 rpm prematuramente.",
      isRecommendedForTopSpeedGoal: false,
    };
  }

  if (rollerWeightGrams <= 17.5) {
    return {
      rollerWeightGrams,
      launchResponse: "ágil / esportiva",
      topSpeedPotential: "padrão OEM (110 km/h)",
      recommendedApplication:
        "Uso misto esportivo. Muito ágil em subidas, mas consome mais em cruzeiro.",
      isRecommendedForTopSpeedGoal: false,
    };
  }

  if (rollerWeightGrams <= 19.5) {
    return {
      rollerWeightGrams,
      launchResponse: "ágil / esportiva",
      topSpeedPotential: "máxima (+10% com polia)",
      recommendedApplication:
        "CALIBRAGEM PERFEITA PARA TOP SPEED: Permite ao motor atingir 8.500 rpm com força centrífuga ideal para abrir 100% da polia CNC.",
      isRecommendedForTopSpeedGoal: true,
    };
  }

  if (rollerWeightGrams <= 21.5) {
    return {
      rollerWeightGrams,
      launchResponse: "equilibrada / original",
      topSpeedPotential: "padrão OEM (110 km/h)",
      recommendedApplication:
        "Padrão de fábrica Honda OEM (21g). Prioriza economia e silêncio com resposta mansa.",
      isRecommendedForTopSpeedGoal: false,
    };
  }

  return {
    rollerWeightGrams,
    launchResponse: "pesada / lerda",
    topSpeedPotential: "comprometida por sobregiro",
    recommendedApplication:
      "Roletes pesados demais forçam overdrive precoce, matando o torque em subidas.",
    isRecommendedForTopSpeedGoal: false,
  };
}

/**
 * Retorna a lista completa de peças de upgrade homologadas
 */
export function getPerformancePartsCatalog(): ReadonlyArray<PerformancePartItem> {
  return ADV150_PERFORMANCE_PARTS;
}

/**
 * Retorna os estágios de preparação da ADV 150
 */
export function getPerformanceStages(): ReadonlyArray<UpgradeStageConfig> {
  return ADV150_UPGRADE_STAGES;
}
